import React, { createContext, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useBusinessTimezone, useClientUser } from '@/hooks';
import { RESOURCE_SCHEDULE_CUTOFF } from '@/constants/resourceScheduling';
import * as Sentry from '@sentry/react';
import { UPSERT_RESOURCES, SET_SCHEDULING_LOCKS, DELETE_RESOURCE } from './graphql/mutations';
import { QUERY_BY_CLIENT as RESOURCES_BY_CLIENT } from '@/graphql/queries/resources';
import { QUERY_BY_CLIENT as SUBREGIONS_BY_CLIENT } from '@/graphql/queries/subregions';
import { QUERY_RESOURCE_DAY_LOAD } from '@/graphql/queries/resource_day_load_view';
import { QUERY_RESOURCE_DAY_BILLING } from '@/graphql/queries/resource_day_billing_view';
import { CALENDAR_VIEWS } from '@onward-delivery/design/calendar';
// The maths, not the component: this subpath is React-free, so the store does not drag the whole
// component barrel into a module that only needs date keys.
import { monthGrid, monthKeyOf, weekGrid } from '@onward-delivery/design/calendar-utils';

import { PRIMARY_TABS } from './constants';
import { useResourceCallbacks, useTodayKey } from './hooks';
import { coreDay } from './utils';
import { resourceDayState } from '@/utilities/resourceDayState';
import { buildCalendarDays, monthStats } from './dayMetrics';

export const Context = createContext();

// The shared constant, not a local subMonths(): `cutoff` is an argument to the rules/overrides
// fields, so it lands in their Apollo storage key on the `resources:<id>` entity this query shares
// with CLIENT_BY_ID's available_resources. A cutoff even a millisecond apart gives that entity two
// separate `overrides(...)` entries, and the cache.updateQuery patches below then write to the one
// the calendar reads and not the one useClientUser() reads — so closing a day here stayed invisible
// to the plan page's booking gate for the rest of the session.
//
// Which is also why it stays frozen at load while `todayKey` is live. A lower bound that ages only
// fetches more history, never less, so a stale one costs a few rows; a fresh one splits the cache.
const cutoff = RESOURCE_SCHEDULE_CUTOFF;

// UPSERT_RESOURCES writes a mapping two ways: nested under insert_resources for a new row, and via
// its own insert_..._mappings field for an update. Top-level mutation fields resolve in document
// order, and added_resource comes first — so the nested lists it returns predate every *_mappings
// field after it. Merging those returned rows back in is what makes an edit visible without a
// refetch; without it, toggling a day that already had an override changed nothing on screen.
const mergeMappings = (existing, added, resourceId) => {
    const byId = new Map((existing || []).map((row) => [row.mapping_id, row]));

    (added?.returning || [])
        .filter((row) => !row.resource_id || row.resource_id === resourceId)
        .forEach((row) => byId.set(row.mapping_id, { ...byId.get(row.mapping_id), ...row }));

    return [...byId.values()];
};

// Each rule carries its own `overrides` list, separate from the resource's, and it is the one
// releaseOverrides reads before a rule is deleted — so patching only the resource's list left a lock
// made in the side dialog invisible to the next OVERWRITE, whose rule delete then hit the rule_id FK.
// Rows join the rule their rule_id names and leave any other; removed ids leave every rule.
// An untouched rule comes back as the same object.
export const syncRuleOverrides = (rules, { added = [], removedIds = new Set() } = {}) =>
    rules?.map((rule) => {
        const own = added.filter((row) => row.rule_id === rule.mapping_id);
        const addedIds = new Set(added.map(({ mapping_id }) => mapping_id));
        const leaves = (row) =>
            removedIds.has(row.mapping_id) ||
            (addedIds.has(row.mapping_id) && !own.some(({ mapping_id }) => mapping_id === row.mapping_id));

        if (!own.length && !(rule.overrides || []).some(leaves)) {
            return rule;
        }

        return {
            ...rule,
            overrides: mergeMappings(rule.overrides, { returning: own }, rule.resource_id).filter(
                (row) => !leaves(row)
            ),
        };
    });

// Evicted, not just filtered out of RESOURCES_BY_CLIENT: the Plan page reads the same entity through
// CLIENT_BY_ID's available_resources, and Apollo drops a dangling reference from every list holding
// it — so a deleted resource stops being bookable there without a reload.
export const evictResources = (cache, rows = []) => {
    rows.forEach((row) => cache.evict({ id: cache.identify(row) }));
    cache.gc();
};

// Edits reach the Plan page through the normalised entity, but a new resource is in no list
// CLIENT_BY_ID fetched, so its reference is appended. Idempotent, so it runs on every save.
//
// Call it after the RESOURCES_BY_CLIENT patch: that write is what puts the cutoff-keyed rules and
// overrides on the entity, which CLIENT_BY_ID selects too — without them its read comes up short.
export const addAvailableResource = (cache, clientId, resource) => {
    if (!clientId || !resource?.resource_id) {
        return;
    }

    cache.modify({
        id: cache.identify({ __typename: 'clients', client_id: clientId }),
        fields: {
            available_resources: (existing = [], { readField, toReference }) =>
                existing.some((ref) => readField('resource_id', ref) === resource.resource_id)
                    ? existing
                    : [...existing, toReference(resource)],
        },
    });
};

// The side dialog's lock Save, both halves: flipped or new closures merged in, dropped ones removed.
export const applySchedulingLocks = (data, { overrides, removed } = {}) => {
    const removedIds = new Set((removed?.returning || []).map(({ mapping_id }) => mapping_id));
    const byResource = (overrides?.returning || []).reduce(
        (acc, row) => ({ ...acc, [row.resource_id]: [...(acc[row.resource_id] || []), row] }),
        {}
    );

    return {
        ...data,
        results: data.results.map((resource) => {
            const added = byResource[resource.resource_id];
            const holds = (list) => (list || []).some(({ mapping_id }) => removedIds.has(mapping_id));
            const touched =
                added || holds(resource.overrides) || (resource.rules || []).some((rule) => holds(rule.overrides));

            if (!touched) {
                return resource;
            }

            return {
                ...resource,
                overrides: mergeMappings(resource.overrides, { returning: added }, resource.resource_id).filter(
                    ({ mapping_id }) => !removedIds.has(mapping_id)
                ),
                rules: syncRuleOverrides(resource.rules, { added, removedIds }),
            };
        }),
    };
};

export const ContextProvider = ({ children }) => {
    const user = useClientUser();
    const [notification, setNotification] = useState({});
    const [primaryTab, setPrimaryTab] = useState(PRIMARY_TABS.CALENDAR);
    // Month or week. The week's anchor is null until the user switches, so it follows today until
    // then, the same way `pinnedMonth` follows the client's zone.
    const [calendarView, setCalendarView] = useState(CALENDAR_VIEWS.MONTH);
    const [pinnedAnchor, setAnchorDate] = useState(null);
    const [editing, editResource] = useState({});
    // Null until the user navigates, so the month follows the client's zone once tz resolves.
    const [pinnedMonth, setMonth] = useState(null);
    // A key, not the payload, so the side dialog re-derives as load and billing arrive.
    const [viewingDayKey, setViewingDayKey] = useState(null);

    // Never null: an unknown zip falls back to the default zone rather than white-screening the page.
    const tz = useBusinessTimezone();

    const todayKey = useTodayKey(tz);
    const month = useMemo(() => pinnedMonth ?? monthKeyOf(todayKey), [pinnedMonth, todayKey]);
    const anchorDate = useMemo(() => pinnedAnchor ?? todayKey, [pinnedAnchor, todayKey]);

    // Flat rather than nested weeks — layout is Calendar's business, and everything here keys by
    // date. Both grids emit the same cell shape, so only this line knows which view is on: the
    // query window, coreDays, buildCalendarDays and the stats all follow from `cells`.
    const cells = useMemo(
        () => (calendarView === CALENDAR_VIEWS.WEEK ? weekGrid(anchorDate).flat() : monthGrid(month).flat()),
        [calendarView, anchorDate, month]
    );

    // The instant-based days `scheduleResource` needs, one per cell.
    const coreDays = useMemo(() => {
        return Object.fromEntries(cells.map(({ dateKey }) => [dateKey, coreDay(dateKey, tz)]));
    }, [cells, tz]);

    const { data: resources, loading: resourcesInFlight } = useQuery(RESOURCES_BY_CLIENT, {
        variables: {
            client_id: user?.user_id,
            cutoff,
        },
    });

    const { data: subregions } = useQuery(SUBREGIONS_BY_CLIENT, {
        variables: {
            client_id: user?.user_id,
        },
    });

    // The Fleet grid has no cell to scope a resource to, and today may not be in the visible grid.
    // With its key, so the config dialog doesn't re-derive the date from the bounds.
    const today = useMemo(() => ({ ...coreDay(todayKey, tz), key: todayKey }), [todayKey, tz]);

    const resourceIds = useMemo(() => {
        return (resources?.results || []).map((resource) => resource.resource_id);
    }, [resources]);

    // A cell's dateKey is the views' delivery_day, so the window is the grid's first and last keys.
    const gridRange = useMemo(() => {
        if (!cells.length) {
            return null;
        }

        return { start: cells[0].dateKey, end: cells[cells.length - 1].dateKey };
    }, [cells]);

    // Invoices generate on a weekly cron, so a future day has nothing to report.
    const billingEnd = useMemo(() => {
        if (!gridRange || gridRange.start > todayKey) {
            return null;
        }

        return gridRange.end > todayKey ? todayKey : gridRange.end;
    }, [gridRange, todayKey]);

    const { data: loadData, loading: loadInFlight } = useQuery(QUERY_RESOURCE_DAY_LOAD, {
        variables: {
            resource_ids: resourceIds,
            start: gridRange?.start,
            end: gridRange?.end,
        },
        skip: !gridRange || resourceIds.length === 0,
    });

    const { data: billingData, loading: billingInFlight } = useQuery(QUERY_RESOURCE_DAY_BILLING, {
        variables: {
            resource_ids: resourceIds,
            start: gridRange?.start,
            end: billingEnd,
        },
        skip: !gridRange || !billingEnd || resourceIds.length === 0,
    });

    const [deleteResource, { loading: deleteResourceInFlight }] = useMutation(DELETE_RESOURCE, {
        update: (cache, { data: { deleted } }) => {
            cache.updateQuery(
                {
                    query: RESOURCES_BY_CLIENT,
                    variables: {
                        client_id: user?.user_id,
                        cutoff,
                    },
                },
                (data) => {
                    let rv = [...data.results];

                    return {
                        ...data,
                        results: rv.filter(
                            (resource) =>
                                !deleted.returning.map((deleted) => deleted.resource_id).includes(resource.resource_id)
                        ),
                    };
                }
            );

            evictResources(cache, deleted.returning);
        },
        onError: (e) => {
            Sentry.captureException(e);
            setNotification({ severity: 'error', message: 'Failed to delete resource' });
        },
    });

    const [upsertResources, { loading: upsertResourceInFlight }] = useMutation(UPSERT_RESOURCES, {
        update: (
            cache,
            {
                data: {
                    added_resource,
                    added_rules,
                    added_overrides,
                    added_regions,
                    removed_rules,
                    removed_overrides,
                    removed_regions,
                },
            }
        ) => {
            cache.updateQuery(
                {
                    query: RESOURCES_BY_CLIENT,
                    variables: {
                        client_id: user?.user_id,
                        cutoff,
                    },
                },
                (data) => {
                    let rv = [...data.results];
                    const idx = rv.findIndex(
                        (resource) => resource.resource_id === added_resource.returning[0].resource_id
                    );
                    let updated = added_resource.returning[0];
                    updated = {
                        ...updated,
                        subregions: mergeMappings(updated.subregions, added_regions, updated.resource_id).filter(
                            (subregion) =>
                                !removed_regions.returning
                                    .map((removed) => removed.mapping_id)
                                    .includes(subregion.mapping_id)
                        ),
                        rules: syncRuleOverrides(
                            mergeMappings(updated.rules, added_rules, updated.resource_id).filter(
                                (rule) =>
                                    !removed_rules.returning
                                        .map((removed) => removed.mapping_id)
                                        .includes(rule.mapping_id)
                            ),
                            {
                                added: added_overrides.returning,
                                removedIds: new Set(removed_overrides.returning.map(({ mapping_id }) => mapping_id)),
                            }
                        ),
                        overrides: mergeMappings(updated.overrides, added_overrides, updated.resource_id).filter(
                            (override) =>
                                !removed_overrides.returning
                                    .map((removed) => removed.mapping_id)
                                    .includes(override.mapping_id)
                        ),
                    };

                    if (idx === -1) {
                        rv = [...rv, updated];
                    } else {
                        rv[idx] = updated;
                    }

                    return {
                        ...data,
                        results: rv,
                    };
                }
            );

            addAvailableResource(cache, user?.user_id, added_resource.returning[0]);
        },
        onError: (e) => {
            Sentry.captureException(e);
            setNotification({ severity: 'error', message: 'Failed to update resource' });
        },
    });

    const [setSchedulingLocks, { loading: setLocksInFlight }] = useMutation(SET_SCHEDULING_LOCKS, {
        update: (cache, { data }) => {
            cache.updateQuery(
                {
                    query: RESOURCES_BY_CLIENT,
                    variables: {
                        client_id: user?.user_id,
                        cutoff,
                    },
                },
                (existing) => applySchedulingLocks(existing, data)
            );
        },
        onError: (e) => {
            Sentry.captureException(e);
            setNotification({ severity: 'error', message: 'Failed to update scheduling' });
        },
    });

    const dayToResource = useMemo(() => {
        const rv = {};
        Object.entries(coreDays).forEach(([dateKey, day]) => {
            (resources?.results || []).forEach((resource) => {
                // `scheduled`, not core's merged flag: a resource closed to new bookings is still
                // working that day and has to stay on the calendar, muted, so the toggle that
                // closed it is still reachable.
                //
                // The whole state rides along rather than being re-derived per cell: this loop
                // already runs resourceDayState() for every resource-day in the grid, and Day.jsx
                // used to run it a second time for each card it drew.
                //
                // All of it, not just `accepting`: the side dialog's lock toggles plan their
                // writes from `coveredByRule` / `blockingOverride` / `ruleEntity`, and resolving
                // those a second time at save time is how the two could disagree.
                const dayState = resourceDayState(resource, day);

                if (dayState.scheduled) {
                    rv[dateKey] = { ...(rv[dateKey] || {}), [resource.resource_id]: { dayState } };
                }
            });
        });

        return rv;
    }, [resources, coreDays]);

    const subregionsById = useMemo(() => {
        return Object.fromEntries(
            (subregions?.results || [])
                .filter((subregion) => !!subregion.is_resource_zone)
                .map((subregion) => [subregion.subregion_id, subregion])
        );
    }, [subregions]);

    const subregionsFiltered = useMemo(() => {
        return (subregions?.results || []).filter((subregion) => !!subregion.is_resource_zone);
    }, [subregions]);

    const resourceById = useMemo(() => {
        return Object.fromEntries((resources?.results || []).map((resource) => [resource.resource_id, resource]));
    }, [resources]);

    const calendar = useMemo(() => {
        return buildCalendarDays({
            cells,
            coreDays,
            dayToResource,
            resourceById,
            loadRows: loadData?.resource_day_load_view,
            billingRows: billingData?.resource_day_billing_view,
            todayKey,
        });
    }, [cells, coreDays, dayToResource, resourceById, loadData, billingData, todayKey]);

    const stats = useMemo(() => monthStats(calendar.monthDays), [calendar]);

    const viewingDay = useMemo(() => {
        if (!viewingDayKey) {
            return null;
        }

        return calendar.daysByKey?.[viewingDayKey] || null;
    }, [viewingDayKey, calendar]);

    const callbacks = useResourceCallbacks({
        upsertResources,
        editResource,
        deleteResource,
        setSchedulingLocks,
    });

    // One memoised value, not a fresh literal every render. Day is wrapped in React.memo and takes
    // its callbacks from here, so a new object on each render re-renders every cell (35 or 42 in a
    // month) — and at 20+ resources a cell is not cheap. Split in three so each dependency list stays
    // readable.
    const state = useMemo(
        () => ({
            primaryTab,
            calendarView,
            anchorDate,
            // The grid reads the same query the calendar does, so switching tabs costs nothing.
            resources: resources?.results || [],
            subregionsOrdered: subregionsFiltered,
            editing,
            notification,
            tz,
            month,
            todayKey,
            dayToResource,
            resourceById,
            subregionsById,
            today,
            days: calendar.daysByKey,
            stats,
            viewingDay,
        }),
        [
            primaryTab,
            calendarView,
            anchorDate,
            resources,
            subregionsFiltered,
            editing,
            notification,
            tz,
            month,
            todayKey,
            dayToResource,
            resourceById,
            subregionsById,
            today,
            calendar,
            stats,
            viewingDay,
        ]
    );

    const loading = useMemo(
        () => ({
            editModal: upsertResourceInFlight || deleteResourceInFlight || setLocksInFlight,
            // The resources query too: the load and billing queries skip until it lands, so without
            // it the first paint read every past cell as "No orders to invoice".
            metrics: resourcesInFlight || loadInFlight || billingInFlight,
        }),
        [
            upsertResourceInFlight,
            deleteResourceInFlight,
            setLocksInFlight,
            resourcesInFlight,
            loadInFlight,
            billingInFlight,
        ]
    );

    // useState setters are stable and useResourceCallbacks memoises its own, so this settles after
    // the first render and never changes again.
    const contextCallbacks = useMemo(
        () => ({
            ...callbacks,
            setPrimaryTab,
            setMonth,
            setCalendarView,
            setAnchorDate,
            viewDay: (dateKey) => {
                setViewingDayKey(dateKey);
            },
            clearViewDay: () => {
                setViewingDayKey(null);
            },
            clearEdit: () => {
                editResource({});
            },
            clearNotification: () => {
                setNotification({});
            },
        }),
        [callbacks]
    );

    const value = useMemo(() => ({ state, loading, callbacks: contextCallbacks }), [state, loading, contextCallbacks]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
};
